import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { wholesaleCustomers, wholesaleOrders } from '../../services/api';
import { TrendingUp, Package, User, ArrowRight, ShoppingBag, Send, CheckCircle } from 'lucide-react';

const formatCurrency = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (d) => new Date(d).toLocaleDateString('en-US');

const STATUS_BADGE = {
  pending:   'bg-warning text-dark',
  confirmed: 'bg-info',
  shipped:   'bg-primary',
  delivered: 'bg-success',
  invoiced:  'bg-secondary',
  paid:      'bg-dark',
  cancelled: 'bg-danger',
};

export default function AccountDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [announcement, setAnnouncement] = useState('');
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Message modal state
  const [showMsgModal, setShowMsgModal] = useState(false);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [msgSent, setMsgSent] = useState(false);

  useEffect(() => {
    Promise.all([
      wholesaleCustomers.getProfile(),
      wholesaleOrders.list(),
      wholesaleCustomers.getAnnouncement(),
    ])
      .then(([profileRes, ordersRes, announcementRes]) => {
        setProfile(profileRes.data.data);
        setOrders(ordersRes.data.orders || []);
        setAnnouncement(announcementRes.data.data || '');
      })
      .catch(() => {
        setError('Failed to load dashboard data. Please try again.');
      })
      .finally(() => setLoading(false));
  }, []);

  const closeMsgModal = () => {
    setShowMsgModal(false);
    setMsgSubject('');
    setMsgBody('');
    setMsgError('');
    setMsgSent(false);
  };

  const handleSendMessage = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) {
      setMsgError('Subject and message are required.');
      return;
    }
    setMsgSending(true);
    setMsgError('');
    try {
      await wholesaleCustomers.sendMessage({ subject: msgSubject.trim(), body: msgBody.trim() });
      setMsgSent(true);
    } catch (err) {
      setMsgError(err?.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setMsgSending(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">{error}</div>
      </div>
    );
  }

  const { companyName, firstName, lastName, email, phone, paymentType, netDays, creditCap, outstandingBalance, availableCredit, rep } = profile;

  const showCredit = (paymentType === 'invoice' || paymentType === 'both') && parseFloat(creditCap) > 0;

  const creditPct = showCredit ? Math.min(100, Math.max(0, (parseFloat(availableCredit) / parseFloat(creditCap)) * 100)) : 0;
  const creditBarColor = creditPct > 50 ? 'bg-success' : creditPct >= 25 ? 'bg-warning' : 'bg-danger';

  const recentOrders = orders.slice(0, 5);

  return (
    <div className="container py-4">

      {/* Announcement banner */}
      {announcement && !announcementDismissed && (
        <div className="alert alert-info alert-dismissible fade show mb-4" role="alert">
          {announcement}
          <button
            type="button"
            className="btn-close"
            onClick={() => setAnnouncementDismissed(true)}
            aria-label="Close"
          />
        </div>
      )}

      {/* Welcome Card */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 p-4">
          <div>
            <h4 className="mb-1 fw-bold">Welcome back, {firstName}!</h4>
            <p className="mb-0 text-muted">{companyName}</p>
          </div>
          <Link to="/wholesale/catalog" className="btn btn-primary d-inline-flex align-items-center gap-2">
            Browse Catalog <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <div className="row g-4">

        {/* Credit Card */}
        {showCredit && (
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom d-flex align-items-center gap-2 py-3">
                <TrendingUp size={18} className="text-primary" />
                <span className="fw-semibold">Account Balance</span>
              </div>
              <div className="card-body p-4">
                <div className="row g-3 mb-3">
                  <div className="col-4 text-center">
                    <div className="small text-muted mb-1">Credit Limit</div>
                    <div className="fw-bold fs-6">{formatCurrency(creditCap)}</div>
                  </div>
                  <div className="col-4 text-center">
                    <div className="small text-muted mb-1">Outstanding</div>
                    <div className="fw-bold fs-6">{formatCurrency(outstandingBalance)}</div>
                  </div>
                  <div className="col-4 text-center">
                    <div className="small text-muted mb-1">Available</div>
                    <div className="fw-bold fs-6">{formatCurrency(availableCredit)}</div>
                  </div>
                </div>
                <div className="progress" style={{ height: '10px' }}>
                  <div
                    className={`progress-bar ${creditBarColor}`}
                    role="progressbar"
                    style={{ width: `${creditPct}%` }}
                    aria-valuenow={creditPct}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
                <div className="text-end mt-1">
                  <small className="text-muted">{creditPct.toFixed(0)}% available</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assigned Rep Card */}
        {rep !== null && rep && (
          <div className={`col-12 ${showCredit ? 'col-lg-6' : 'col-lg-4'}`}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom d-flex align-items-center gap-2 py-3">
                <User size={18} className="text-primary" />
                <span className="fw-semibold">Your Account Rep</span>
              </div>
              <div className="card-body p-4 d-flex flex-column justify-content-center gap-3">
                <div>
                  <div className="fw-semibold fs-6 mb-1">{rep.firstName} {rep.lastName}</div>
                  <a href={`mailto:${rep.email}`} className="text-decoration-none text-primary small">{rep.email}</a>
                </div>
                <button
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2 align-self-start"
                  onClick={() => setShowMsgModal(true)}
                >
                  <Send size={14} /> Send a Message
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recent Orders Card */}
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom d-flex align-items-center gap-2 py-3">
              <Package size={18} className="text-primary" />
              <span className="fw-semibold">Recent Orders</span>
            </div>
            <div className="card-body p-0">
              {recentOrders.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                  <ShoppingBag size={40} className="mb-3 opacity-50" />
                  <p className="mb-0">No orders yet</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4">Order #</th>
                        <th>Date</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th className="pe-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.orderID}>
                          <td className="ps-4 fw-semibold">#{order.orderID}</td>
                          <td className="text-muted">{formatDate(order.orderDate)}</td>
                          <td>{formatCurrency(order.totalAmount)}</td>
                          <td>
                            <span className={`badge ${STATUS_BADGE[order.orderStatus] || 'bg-secondary'}`}>
                              {order.orderStatus ? order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1) : '—'}
                            </span>
                          </td>
                          <td className="pe-4 text-end">
                            <Link to={`/wholesale/orders/${order.orderID}`} className="btn btn-sm btn-outline-primary">
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Send Message Modal */}
      {showMsgModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          aria-modal="true"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !msgSending) closeMsgModal(); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <Send size={16} /> Contact Your Rep
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeMsgModal}
                  disabled={msgSending}
                />
              </div>
              <div className="modal-body">
                {msgSent ? (
                  <div className="text-center py-3">
                    <CheckCircle size={44} className="text-success mb-3" />
                    <p className="fs-5 fw-semibold mb-1">Message sent!</p>
                    <p className="text-muted small mb-0">
                      {rep?.firstName} will receive your message and can reply directly to your email.
                    </p>
                  </div>
                ) : (
                  <>
                    {msgError && (
                      <div className="alert alert-danger py-2">{msgError}</div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">To</label>
                      <input
                        type="text"
                        className="form-control"
                        value={rep ? `${rep.firstName} ${rep.lastName}` : ''}
                        disabled
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Subject</label>
                      <input
                        type="text"
                        className="form-control"
                        value={msgSubject}
                        onChange={(e) => setMsgSubject(e.target.value)}
                        placeholder="What's this about?"
                        maxLength={255}
                        autoFocus
                      />
                    </div>
                    <div className="mb-0">
                      <label className="form-label">Message</label>
                      <textarea
                        className="form-control"
                        rows={5}
                        value={msgBody}
                        onChange={(e) => setMsgBody(e.target.value)}
                        placeholder="Type your message here…"
                      />
                    </div>
                  </>
                )}
              </div>
              {!msgSent && (
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={closeMsgModal}
                    disabled={msgSending}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                    onClick={handleSendMessage}
                    disabled={msgSending}
                  >
                    {msgSending
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : <Send size={14} />
                    }
                    Send
                  </button>
                </div>
              )}
              {msgSent && (
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closeMsgModal}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
